import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Button, LinearProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { useStore } from 'src/stores';
import { ACCEPTED_BILL_FILE_TYPES, BULK_UPLOAD_STATUS, MAX_BULK_UPLOAD_FILES, MAX_BULK_UPLOAD_FILE_MB } from 'src/stores/billAutomation/bulkUpload';
import { getPropertyAddressIndex, mapCompanyBillToRow } from './companyBillRows';
import useStyles from './uploadBillModalStyles';
const CHIP = {
  extracting: {
    label: 'Extracting…',
    tone: 'info'
  },
  processed: {
    label: 'Processed',
    tone: 'success'
  },
  needsReview: {
    label: 'Needs review',
    tone: 'warning'
  },
  error: {
    label: 'Error',
    tone: 'error'
  },
  duplicate: {
    label: 'Already uploaded',
    tone: 'warning'
  }
};
const formatMB = mb => "".concat(mb.toFixed(1), " MB");

/**
 * What a landed file's row says, read from the bills list — the same rows the table behind renders,
 * mapped through the same mapper. A bill's progress is the bill's own, so reading it from anywhere
 * else is how the modal ends up insisting a file is still pending while the table has it validated.
 */
const describeLandedBill = (row, addressById) => {
  if (!row) return {
    chip: CHIP.extracting,
    detail: 'uploaded'
  };
  const mapped = mapCompanyBillToRow(row, addressById);
  const address = (mapped.propertiesLines || [])[0];
  const utilities = (mapped.utilityTypes || []).join(', ');
  switch (row.extractStatus) {
    case 'Validated':
      return {
        chip: CHIP.processed,
        detail: address ? "attached to ".concat(address).concat(utilities ? " \xB7 ".concat(utilities) : '') : 'processed'
      };
    case 'NeedsProperty':
      // The list carries no reason, so it says what to do rather than guessing why.
      return {
        chip: CHIP.needsReview,
        detail: 'no property matched — open the bill to pick one'
      };
    case 'Failed':
      return {
        chip: CHIP.error,
        detail: 'extraction failed — open the bill for details'
      };
    case 'Duplicate':
      // Server-side dedup after landing (same account + period, LB-5779); the tooltip carries its reason.
      return {
        chip: CHIP.duplicate,
        detail: mapped.statusTooltip || 'already received for this company'
      };
    case 'Rejected':
      return {
        chip: CHIP.error,
        detail: mapped.statusTooltip || 'rejected — open the bill for details'
      };
    default:
      return {
        chip: CHIP.extracting,
        detail: 'uploaded'
      };
  }
};

/** What one queue row shows: a chip and a line under the name, or a progress bar while it is going up. */
const describeEntry = (entry, billRow, addressById) => {
  switch (entry.status) {
    case BULK_UPLOAD_STATUS.uploading:
      return {
        isUploading: true,
        detail: null,
        chip: null
      };
    case BULK_UPLOAD_STATUS.duplicate:
      return {
        chip: CHIP.duplicate,
        detail: 'already uploaded for this company'
      };
    case BULK_UPLOAD_STATUS.error:
      return {
        chip: CHIP.error,
        detail: entry.error
      };
    case BULK_UPLOAD_STATUS.landed:
      return describeLandedBill(billRow, addressById);
    default:
      return {
        chip: null,
        detail: 'waiting'
      };
  }
};

/**
 * Drops bill files in and watches them through extraction. Every file becomes its own bill and goes
 * up on its own request, so one slow or bad file holds up nothing else, and the queue lives in the
 * store rather than here — the modal can be closed while files are still going up.
 *
 * No property is chosen here. The pipeline matches a bill to its property from the service address
 * on the document; one it cannot place lands in review, which is a better answer than a guess made
 * at upload time by someone reading a file name.
 */
export const UploadBillModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    handleClose = _ref.handleClose;
  const classes = useStyles();
  const store = useStore();
  const fileInputRef = useRef(null);
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDraggingOver = _useState2[0],
    setIsDraggingOver = _useState2[1];
  const queue = store.bulkUpload;
  const companyName = localStorage.getItem(USER_SETTINGS_KEYS.companyName) || '';
  const addressById = getPropertyAddressIndex(store.company.propertiesList);
  useEffect(() => {
    if (isOpen) return;

    // What finished has been read; what has not is still going up and stays for the next open.
    queue.clearSettledEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const acceptFiles = fileList => {
    const files = Array.from(fileList || []).filter(file => file instanceof File);
    if (!files.length) return;
    const _queue$addFiles = queue.addFiles(files),
      rejected = _queue$addFiles.rejected;
    if (rejected > 0) toast('warning', {
      body: ["Up to ".concat(MAX_BULK_UPLOAD_FILES, " files can be uploaded at once.")]
    });

    // Nothing to confirm — there is no per-file choice left to make, so they start straight away.
    queue.startProcessing();
  };
  const handleBrowse = event => {
    acceptFiles(event.target.files);
    // Lets the same file be picked twice in a row, which otherwise fires no change event.
    event.target.value = '';
  };
  const handleDrop = event => {
    var _event$dataTransfer;
    event.preventDefault();
    setIsDraggingOver(false);
    acceptFiles((_event$dataTransfer = event.dataTransfer) === null || _event$dataTransfer === void 0 ? void 0 : _event$dataTransfer.files);
  };
  const entries = [...queue.entries];
  return /*#__PURE__*/React.createElement(SimpleModal, {
    title: companyName ? "Upload bills \xB7 ".concat(companyName) : 'Upload bills',
    isOpen: isOpen,
    handleClose: handleClose
    // Without this the modal rebuilds its children on every render and the inputs lose focus.
    ,
    isMemoChildren: true,
    variant: "middle",
    leftButton: /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      onClick: () => queue.cancelQueue(),
      disabled: !queue.hasEntries,
      'custom-attribute': 'bills-upload-bill-modal-cancel-queue'
    }, "Cancel queue"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleClose,
      'custom-attribute': 'bills-upload-bill-modal-done'
    }, "Done")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.notice
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "info",
    state: "info",
    fontSize: "small"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "Each file is extracted automatically. We auto-attach to the right property when we recognize the address or utility account number \u2014 anything unrecognized lands in ", /*#__PURE__*/React.createElement("strong", null, "Needs review"), ".")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "".concat(classes.dropzone, " ").concat(isDraggingOver ? classes.dropzoneActive : ''),
    onClick: () => {
      var _fileInputRef$current;
      return (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click();
    },
    onDragOver: event => {
      event.preventDefault();
      setIsDraggingOver(true);
    },
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: handleDrop,
    'custom-attribute': 'bills-upload-bill-modal-dropzone'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "upload",
    state: "grey"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.dropzoneTitle
  }, "Drop bills here, or ", /*#__PURE__*/React.createElement("span", {
    className: classes.dropzoneBrowse
  }, "browse")), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.dropzoneHint
  }, "PDF, PNG, JPG, GIF, WebP \xB7 up to ", MAX_BULK_UPLOAD_FILE_MB, " MB each \xB7 multi-select OK")), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    className: classes.hiddenInput,
    type: "file",
    multiple: true,
    accept: ACCEPTED_BILL_FILE_TYPES,
    onChange: handleBrowse
  }), entries.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.queueLabel
  }, entries.length, " file", entries.length === 1 ? '' : 's', " queued"), /*#__PURE__*/React.createElement("div", {
    className: classes.fileList
  }, entries.map(entry => {
    const billRow = entry.billId ? store.companyBills.getBill(entry.billId) : null;
    const _describeEntry = describeEntry(entry, billRow, addressById),
      chip = _describeEntry.chip,
      detail = _describeEntry.detail,
      isUploading = _describeEntry.isUploading;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.fileRow,
      key: entry.id
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "file-text",
      state: "error"
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.fileMain
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileName,
      title: entry.fileName
    }, entry.fileName), isUploading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LinearProgress, {
      variant: "determinate",
      value: Math.round(entry.progress * 100),
      className: classes.fileProgress
    }), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileDetail
    }, "Uploading ".concat(Math.round(entry.progress * 100), "% \xB7 ").concat(formatMB(entry.sentMB), " / ").concat(formatMB(entry.fileSizeMB)))) : /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.fileDetail
    }, "".concat(formatMB(entry.fileSizeMB)).concat(detail ? " \xB7 ".concat(detail) : ''))), chip && /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.statusChip, " ").concat(classes["chip_".concat(chip.tone)])
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.statusDot
    }), chip.label));
  }))), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.backgroundHint
  }, "You can close this \u2014 uploads continue in the background."));
});